import type { ReactNode } from 'react';
import { COVERAGE_GATE } from '@gitrader/shared';

/**
 * The help content, as data.
 *
 * One registry, rendered in two places: the slide-over panel (contextual, opened at the topic
 * for the page you are on or by a ? beside a particular panel) and the full reference at /help.
 * Writing it twice would guarantee the two disagreed within a month.
 *
 * ## Who this is written for
 *
 * A collaborator who was not in the conversations, and who may be shown a screen by Massimo
 * without warning. So: domain vocabulary is explained the first time it appears, and the main
 * prose stays free of migrations, schema versions and job kinds. The internals a collaborator
 * genuinely needs are in `detail` sections, which render as a set-aside "Under the hood" block —
 * present for whoever wants them, skippable by everyone else.
 *
 * ## The rule that keeps this honest
 *
 * `help.test.ts` asserts that every route in the tree resolves to a topic, that every topic a
 * `HelpTip` names exists, and that `related` only points at real topics. Documentation drifts
 * when nothing fails as it drifts.
 */

export type HelpSection =
  | { kind: 'prose'; heading?: string; body: ReactNode }
  | { kind: 'terms'; heading?: string; items: readonly { term: string; body: ReactNode }[] }
  | { kind: 'steps'; heading?: string; items: readonly ReactNode[] }
  /** Rendered as a de-emphasised "Under the hood" aside. Internals, not the main thread. */
  | { kind: 'detail'; heading: string; body: ReactNode };

export interface HelpTopic {
  id: string;
  title: string;
  /** One or two sentences: what this is and what it is for. */
  summary: string;
  /** The route path this topic is the default help for, if any. Matched most-specific-first. */
  route?: string;
  sections: readonly HelpSection[];
  related?: readonly string[];
}

const GATE_PCT = `${COVERAGE_GATE * 100}%`;

// ---- concepts -------------------------------------------------------------------------------

const CONCEPTS: readonly HelpTopic[] = [
  {
    id: 'concepts.overview',
    title: 'What GiTrader is',
    summary:
      'A personal, AI-assisted day-trading system. It is being built back-to-front: the data and the judgement first, the trading last, so that nothing downstream is trained on numbers nobody checked.',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            The end goal is a system that helps decide what to trade on a given day and executes
            through a broker on an explicit command, with strict stop losses and trailing take
            profits. Nothing about trading is switched on yet, and that is deliberate: a model is
            only as good as the data and the labels underneath it, so those are being built and
            verified first.
          </>
        ),
      },
      {
        kind: 'steps',
        heading: 'The pipeline, in order',
        items: [
          <>
            <b>Ingest.</b> Pull 1-minute bars for a handful of liquid symbols from the market data
            provider, and record how complete each trading session actually is.
          </>,
          <>
            <b>Measure.</b> Judge every session against the trading calendar. A session missing too
            many of its minutes is excluded from everything downstream rather than quietly used.
          </>,
          <>
            <b>Compute indicators.</b> One implementation of every indicator, stored once and read
            by both the charts and the research code, so the two cannot disagree.
          </>,
          <>
            <b>Label.</b> Mark the trades that were there to be taken, by hand, on the chart. This
            is the part that is happening now.
          </>,
          <>
            <b>Train and evaluate.</b> Build datasets from those labels, fit models, and measure
            them honestly on data they have never seen.
          </>,
          <>
            <b>Trade.</b> Paper first, then live, always on an explicit command.
          </>,
        ],
      },
      {
        kind: 'prose',
        heading: 'Why so much of the interface is about data quality',
        body: (
          <>
            Most of what is built so far measures whether the data can be trusted, because the
            failure it is guarding against is silent. A chart drawn from 87% of its minutes looks
            completely normal. A model trained on it looks fine too — right up to the point where it
            is trading money. Every gate and warning in this application exists because the
            alternative is finding out late.
          </>
        ),
      },
    ],
    related: ['concepts.coverage', 'concepts.labels'],
  },

  {
    id: 'concepts.sessions',
    title: 'Sessions, dates and time zones',
    summary:
      'Everything is organised by trading session — one day on one exchange — and every clock time you see is New York time, because that is the market being traded.',
    sections: [
      {
        kind: 'terms',
        items: [
          {
            term: 'Trading session',
            body: (
              <>
                One day the market was open. Regular hours are 09:30 to 16:00 New York, which is 390
                minutes, or 78 five-minute bars when every minute prints.
              </>
            ),
          },
          {
            term: 'Early close',
            body: (
              <>
                A half day — the afternoon before a holiday, typically closing at 13:00. These are
                real sessions with fewer minutes, and treating them as short normal days is a
                classic way to generate false alarms about missing data.
              </>
            ),
          },
          {
            term: 'Session date',
            body: (
              <>
                The calendar day in New York, not in UTC. A bar at 20:30 UTC belongs to that
                afternoon&apos;s session, not to the next day.
              </>
            ),
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'Why the calendar is a table and not a calculation',
        body: (
          <>
            Which days the market was open, and until when, comes from a stored calendar supplied by
            the broker — never from weekday arithmetic. Holidays move, half days are announced, and
            daylight saving shifts New York and Europe on different dates. Any of those computed
            rather than looked up is a bug that only appears a few times a year, which is the worst
            frequency: often enough to corrupt the data, rarely enough to escape notice.
          </>
        ),
      },
    ],
    related: ['page.data.calendar', 'concepts.coverage'],
  },

  {
    id: 'concepts.coverage',
    title: 'Coverage and the gate',
    summary: `How complete a session's data is: the share of its regular-hours minutes that actually have a price. Below ${GATE_PCT} a session is held back from labelling and from datasets.`,
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            The data feed does not print a bar for every minute. When a stock trades rarely enough
            in a given minute, that minute simply has no data. Coverage is the fraction of the
            session&apos;s 390 regular minutes that do have data — so 0.997 means three minutes
            missing across the whole day, and 0.87 means about fifty.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: `Why ${GATE_PCT}, and why it is a refusal rather than a warning`,
        body: (
          <>
            A five-minute chart built from 87% of its minutes looks entirely normal — the candles
            are all there, they are just each built from fewer trades, and some are stale by a few
            minutes. Nothing about looking at it tells you. So sessions below the gate are excluded
            by default: from the labelling queue, and from the datasets built afterwards. Labelling
            one anyway is possible, but it requires typing a reason, and that reason is stored
            against every label written on that session.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'Coverage is a property of the symbol, not just the feed',
        body: (
          <>
            It is governed by how <i>evenly</i> trades arrive, not simply how many. Two symbols with
            the same daily volume can differ sharply if one trades in bursts. This is why a new
            symbol has to be measured rather than assumed: NVIDIA&apos;s coverage jumped from
            unusable to near-perfect on the day of its 2024 stock split, while the index funds used
            as controls did not move at all on the same day.
          </>
        ),
      },
      {
        kind: 'detail',
        heading: 'Under the hood',
        body: (
          <>
            Coverage is stored per (symbol, date, feed, adjustment) in{' '}
            <code>market.session_coverage</code>, with both a regular-hours and an extended-hours
            figure. The gate constant is <code>COVERAGE_GATE</code> in the shared package, so the
            chart, the queue and the database check the same number. A session with no coverage row
            at all is treated as below the gate and shown as &ldquo;no data&rdquo; — distinct from
            0%, which would imply it was measured.
          </>
        ),
      },
    ],
    related: ['page.data.quality', 'page.data.instruments', 'concepts.labels'],
  },

  {
    id: 'concepts.feeds',
    title: 'Feeds and price adjustment',
    summary:
      'Every price in the system is qualified by which feed it came from and how it was adjusted for corporate actions. The same instant can have different legitimate prices.',
    sections: [
      {
        kind: 'terms',
        items: [
          {
            term: 'IEX',
            body: (
              <>
                The feed in use. It carries trades from one exchange only, roughly two to three
                percent of total volume, which is why coverage matters so much here. It is included
                with the broker&apos;s free data plan.
              </>
            ),
          },
          {
            term: 'SIP',
            body: (
              <>
                The consolidated feed — every exchange, effectively complete coverage. A paid
                upgrade, and the obvious answer if IEX proves too thin for the symbols being traded.
                Deciding that is a measurement still outstanding.
              </>
            ),
          },
          {
            term: 'Split adjustment',
            body: (
              <>
                Prices restated so that a stock split does not appear as a crash. Everything stored
                here is split-adjusted, which keeps charts continuous across a split and is what
                indicators are computed from.
              </>
            ),
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'Why the qualifier travels with the price',
        body: (
          <>
            A price of 118.00 might sit inside a bar on the split-adjusted series and nowhere near
            it on the raw one. Any stored price that does not say which series it belongs to cannot
            be checked later, so feed and adjustment are part of the key on bars, coverage and
            indicators alike.
          </>
        ),
      },
    ],
    related: ['concepts.coverage', 'page.admin.connections'],
  },

  {
    id: 'concepts.indicators',
    title: 'Indicators and versions',
    summary:
      'The moving averages, VWAP, ATR and the rest are computed once, stored, and read by both the charts and the research code. The charts never calculate anything themselves.',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            There is exactly one implementation of every indicator. The chart draws stored columns;
            if a column is missing the line simply does not appear. This rules out an entire class
            of bug where the chart shows one number and the model trains on another — they cannot
            disagree, because only one of them does the arithmetic.
          </>
        ),
      },
      {
        kind: 'terms',
        heading: 'What you will see referenced',
        items: [
          {
            term: 'VWAP',
            body: <>Volume-weighted average price for the session so far.</>,
          },
          {
            term: 'EMA 9 / 21 / 50',
            body: <>Exponential moving averages over that many bars.</>,
          },
          {
            term: 'ATR',
            body: (
              <>
                Average True Range — how far this instrument typically moves in a bar. It is the
                unit risk is measured in: a stop placed 1.5 ATR away means the same thing on a
                volatile stock as on a quiet one.
              </>
            ),
          },
          {
            term: 'RSI, MACD, Stochastic',
            body: <>Oscillators, shown one at a time in the lower pane of the chart.</>,
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'Versions',
        body: (
          <>
            When an indicator&apos;s definition changes, the new values are written alongside the
            old ones under a new version number rather than replacing them. Any dataset records the
            version it was built from, so an experiment stays reproducible even after the
            definitions move on. The chart footer shows which version it is displaying.
          </>
        ),
      },
      {
        kind: 'detail',
        heading: 'Under the hood',
        body: (
          <>
            141 columns in <code>market.indicator_5m</code>, keyed by (symbol, bucket, feed,
            adjustment, <code>indicator_version</code>). Values are fixed-point decimals rather than
            floats, because the two readers are Node and Python and the likeliest way they disagree
            is text rendering of the same double. Version 4 is current. Every feature is covered by
            a leakage test that recomputes it from a truncated dataset and requires byte-identical
            output — nothing may see the future, however indirectly.
          </>
        ),
      },
    ],
    related: ['page.analysis.labeling', 'concepts.coverage'],
  },

  {
    id: 'concepts.labels',
    title: 'Labels: the dataset being built',
    summary:
      'A label is one marked trade — an entry bar, an exit bar, a direction, how confident you were and what kind of setup it was. These are what the models learn from.',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            Marking trades by hand is the current work. Each label records where a trade could have
            been entered and exited, in which direction, with a confidence from 1 to 5 and at least
            one setup tag from a fixed vocabulary. The tags are a closed list on purpose: free text
            becomes unusable within a week, when &ldquo;orb&rdquo;, &ldquo;ORB&rdquo; and
            &ldquo;opening range breakout&rdquo; are three different things to a computer and one
            thing to a person.
          </>
        ),
      },
      {
        kind: 'terms',
        heading: 'Two ways of marking',
        items: [
          {
            term: 'Hindsight',
            body: (
              <>
                Marking with the whole day visible. This produces the <i>ceiling</i> — what was
                achievable, not what was decidable at the time. It is the fastest way to build
                volume and it is systematically optimistic, which is fine as long as nobody pretends
                otherwise.
              </>
            ),
          },
          {
            term: 'Replay',
            body: (
              <>
                Marking bar by bar with the future hidden, as it would have looked live. Slower and
                honest. The gap between the two numbers is the most useful measurement this project
                will produce: it is the size of your own hindsight bias, quantified.
              </>
            ),
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'The decision window',
        body: (
          <>
            Only bars between 09:45 and 15:45 New York can be labelled. The opening and closing
            fifteen minutes are excluded because they behave differently from the rest of the day —
            the open is auction noise and the close is position-squaring — and a dataset that mixes
            them in teaches a model that those minutes look normal. On a half day the window closes
            earlier.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'Nothing is ever deleted',
        body: (
          <>
            Labels can be edited and deleted from the interface, and behave exactly as you would
            expect: a corrected label replaces the old one in the list, a deleted one disappears
            from it and from every dataset. Underneath, both are recorded rather than destroyed. How
            your judgement changed over a labelling period is itself a measurement, and the labels
            you withdrew are the most informative part of it.
          </>
        ),
      },
      {
        kind: 'detail',
        heading: 'Under the hood',
        body: (
          <>
            <code>labeling.trade_label</code> is append-only, enforced by a database trigger:
            deletes are refused and every column is frozen except the supersession and retraction
            marks. An edit writes a replacement row and points the old one at it; a delete sets a
            dated retraction mark. Asking for the label set <i>as of</i> a past instant returns what
            it was then, which is what makes &ldquo;this model was trained on these labels&rdquo; a
            checkable claim rather than a recollection.
          </>
        ),
      },
    ],
    related: ['page.analysis.labeling', 'concepts.coverage', 'concepts.indicators'],
  },

  {
    id: 'concepts.jobs',
    title: 'Jobs and the worker',
    summary:
      'Anything slow — fetching bars, computing indicators, checking a credential — runs as a job on a background worker rather than in the browser. You enqueue it and watch it.',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            The application is two processes sharing one database: the API that serves this
            interface, and a worker that does the long-running work. Enqueuing a job returns
            immediately; the job then runs on the worker, streams its log, and reports progress.
            Closing the browser does not stop it.
          </>
        ),
      },
      {
        kind: 'terms',
        heading: 'Job states',
        items: [
          { term: 'Queued', body: <>Waiting for a worker to pick it up.</> },
          { term: 'Running', body: <>In progress, with a live log and a progress count.</> },
          {
            term: 'Cancelling',
            body: (
              <>
                You asked it to stop. It stops at its next checkpoint rather than being killed, so
                it never leaves a half-written batch behind.
              </>
            ),
          },
          {
            term: 'Succeeded / Failed / Cancelled',
            body: <>Finished, with a result or an error, and a record of who ended it.</>,
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'Why a job can be taken away from a worker',
        body: (
          <>
            A running job holds a lease that it renews while it works. If a worker dies, the lease
            expires and the job returns to the queue for someone else rather than being stuck
            &ldquo;running&rdquo; forever. A worker that has lost its lease abandons the job without
            writing anything to it.
          </>
        ),
      },
    ],
    related: ['page.data.jobs', 'page.admin.system', 'page.data.ingestion'],
  },

  {
    id: 'concepts.paper-live',
    title: 'Paper and live',
    summary:
      'The badge in the top bar says which environment the system is pointed at. It is paper — simulated money — and live trading is refused everywhere in the current milestone.',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            Paper trading runs against the broker&apos;s simulator with the same API as the real
            thing: orders, fills and positions behave realistically and no money moves. The
            environment is part of the stored credential, so it cannot be switched by accident from
            a form field, and the current milestone refuses a live environment outright rather than
            trusting that nobody will select it.
          </>
        ),
      },
      {
        kind: 'prose',
        body: (
          <>
            When live trading does arrive it will be behind an explicit command each time, with
            mandatory stop losses. Nothing will trade on a schedule.
          </>
        ),
      },
    ],
    related: ['page.admin.connections'],
  },
];

// ---- pages ----------------------------------------------------------------------------------

const PAGES: readonly HelpTopic[] = [
  {
    id: 'page.home',
    title: 'Overview',
    summary: 'The landing page: where the build has got to, and what each area is for.',
    route: '/',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            Sections marked <b>SOON</b> are not built yet and say what will fill them. They are
            shown rather than hidden so the shape of the finished system is visible, and so a
            missing feature never looks like a broken one.
          </>
        ),
      },
    ],
    related: ['concepts.overview'],
  },

  {
    id: 'page.data.instruments',
    title: 'Instruments',
    summary:
      'Which symbols are loaded, how complete each one is, and what role it plays. The starting point for judging whether a symbol is usable.',
    route: '/data/instruments',
    sections: [
      {
        kind: 'terms',
        heading: 'The two roles',
        items: [
          {
            term: 'Labelled',
            body: (
              <>
                The stocks actually being studied and eventually traded — currently Tesla, NVIDIA,
                Apple, Palantir and others under evaluation. Chosen for volatility and for having a
                thick enough tape to give usable coverage.
              </>
            ),
          },
          {
            term: 'Context',
            body: (
              <>
                Index and sector funds — SPY, QQQ, IWM and the sector ETFs. These are never traded.
                They exist so that a stock&apos;s move can be read against its market and its
                sector: a stock up 1% on a day its sector is up 2% is a different event from the
                same 1% on a flat day.
              </>
            ),
          },
        ],
      },
      {
        kind: 'terms',
        heading: 'The columns',
        items: [
          {
            term: 'Sessions',
            body: (
              <>
                Loaded against expected. Expected comes from the trading calendar between the first
                and last session you have, so a gap in the middle shows up as a shortfall.
              </>
            ),
          },
          {
            term: 'Mean / min coverage',
            body: (
              <>
                Average and worst completeness across the loaded range. A high mean with a very low
                minimum means the problem is concentrated in a few sessions rather than spread out.
              </>
            ),
          },
          {
            term: `Below ${GATE_PCT}`,
            body: <>How many sessions fall under the gate and are therefore excluded by default.</>,
          },
          {
            term: 'Enabled',
            body: (
              <>
                Whether scheduled work considers this symbol. Disabling keeps the data and stops the
                jobs.
              </>
            ),
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'Feed, adjustment and threshold',
        body: (
          <>
            The controls above the tables change what is being judged, not what is stored. The
            threshold is what counts as a complete session for the columns below it; the gate used
            elsewhere in the application is fixed at {GATE_PCT}.
          </>
        ),
      },
    ],
    related: ['concepts.coverage', 'concepts.feeds', 'page.data.quality'],
  },

  {
    id: 'page.data.ingestion',
    title: 'Ingestion',
    summary:
      'Fetch market data and run the other data operations. For bars you see a plan of exactly which sessions will be fetched before anything is enqueued.',
    route: '/data/ingestion',
    sections: [
      {
        kind: 'prose',
        heading: 'Plan first',
        body: (
          <>
            Choosing a symbol and a range produces a list of the sessions that would be fetched, and
            why. Coverage decides that list — not a high-water mark — so a session that was fetched
            before but came back incomplete is offered again, and one that is already complete is
            skipped. A cursor would have silently skipped it forever.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'Then enqueue',
        body: (
          <>
            Confirming hands the work to the worker as a job. You can leave the page; progress and
            the log are on the Jobs page, and the job continues regardless.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'The other operations',
        body: (
          <>
            The same form runs the rest of the data operations — refreshing the trading calendar,
            recomputing indicators for a symbol and range, recomputing quality figures. Each one
            declares what it needs and the form is built from that declaration, so what you see is
            what the worker will actually accept.
          </>
        ),
      },
    ],
    related: ['concepts.jobs', 'concepts.coverage', 'page.data.jobs'],
  },

  {
    id: 'page.data.jobs',
    title: 'Jobs',
    summary:
      'Every operation that has been run, with its status, its log, who ended it and how. Click a row for the detail and the live log.',
    route: '/data/jobs',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            A running job streams its log as it goes and reports how far through it is. Cancelling
            asks it to stop at its next checkpoint — it is never killed mid-write, so a cancelled
            ingestion leaves the sessions it had already finished intact and nothing half-written.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'Secrets never appear here',
        body: (
          <>
            Job logs are scrubbed before they are stored, both raw and URL-encoded, so an API key
            passed to a broker call cannot surface in a log line or an error message.
          </>
        ),
      },
    ],
    related: ['concepts.jobs', 'page.admin.system'],
  },

  {
    id: 'page.data.quality',
    title: 'Data quality',
    summary:
      'Coverage over time for one symbol: the shape of its completeness, which sessions fall short, and by how much.',
    route: '/data/quality',
    sections: [
      {
        kind: 'terms',
        heading: 'What the figures mean',
        items: [
          { term: 'Sessions', body: <>How many sessions are loaded in the range being viewed.</> },
          {
            term: 'Mean coverage',
            body: <>Average completeness across them.</>,
          },
          {
            term: 'Regular minutes',
            body: (
              <>
                Minutes with data inside regular hours, against how many were expected. This is the
                raw count behind the percentage.
              </>
            ),
          },
          {
            term: 'Below gate',
            body: (
              <>
                Sessions under {GATE_PCT}, which are the ones excluded from labelling and from
                datasets.
              </>
            ),
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'How to read the series',
        body: (
          <>
            A steady line near the top is what a usable symbol looks like. A step change usually
            means something happened to the instrument rather than to the feed — a stock split, or a
            jump in how actively it trades. Isolated dips are worth checking against the same day on
            another symbol: if every symbol dips together it is a feed outage, not a property of the
            stock.
          </>
        ),
      },
    ],
    related: ['concepts.coverage', 'page.data.instruments'],
  },

  {
    id: 'page.data.calendar',
    title: 'Trading calendar',
    summary:
      'Which days the market was open and until when. Every session boundary in the system comes from here.',
    route: '/data/calendar',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            Supplied by the broker and stored, rather than computed from weekdays. Half days and
            holidays are facts to be looked up; treating them as arithmetic produces errors a few
            times a year, which is exactly often enough to corrupt a dataset and exactly rare enough
            to go unnoticed.
          </>
        ),
      },
      {
        kind: 'terms',
        items: [
          {
            term: 'Early closes',
            body: <>Half days, which have fewer expected minutes and a shorter decision window.</>,
          },
          {
            term: 'DST transitions',
            body: (
              <>
                The days New York changes clocks. Europe changes on different dates, so for a couple
                of weeks each year the offset between here and the market is not what it usually is.
              </>
            ),
          },
        ],
      },
    ],
    related: ['concepts.sessions', 'concepts.coverage'],
  },

  {
    id: 'page.analysis.labeling',
    title: 'Labeling',
    summary:
      'The chart the dataset is built on. Mark where a trade could have been entered and exited, and commit it. Designed to be driven entirely from the keyboard.',
    route: '/analysis/labeling',
    sections: [
      {
        kind: 'steps',
        heading: 'The loop',
        items: [
          <>
            Move the cursor to the entry bar with <b>←</b> and <b>→</b>, and press <b>E</b>.
          </>,
          <>
            Move to the exit bar and press <b>X</b>.
          </>,
          <>
            Press <b>Enter</b> to commit.
          </>,
          <>
            Direction, confidence and tags carry over from the previous trade, so you only press
            them when they change. Press <b>?</b> for the full key list and a legend of every mark
            on the chart.
          </>,
        ],
      },
      {
        kind: 'prose',
        heading: 'What the numbers under the chart mean',
        body: (
          <>
            Once both ends are marked you see the return, the R multiple, and the best and worst the
            trade ever looked while it was open. <b>R</b> is the profit measured in units of risk:
            1.5 ATR from the entry is one R, so +2R means the trade made twice what it was risking.
            It is the only way to compare a trade on a $400 stock with one on a $30 stock.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'These are hindsight numbers',
        body: (
          <>
            Marked with the whole session visible, they describe what was achievable rather than
            what was decidable at 10:15 with the afternoon still unknown. The panel says so
            permanently, because the moment anyone forgets it, a backtest built on these labels
            starts looking extremely good for the wrong reason.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'Editing and deleting',
        body: (
          <>
            Every committed label is listed under the chart with <b>Edit</b> and <b>Delete</b>.
            Editing loads it back onto the chart so you can move the marks and commit the
            correction; the reason is written for you from what changed and stays editable. Deleting
            asks why, and tells you plainly what happens: the label stops counting and stays on the
            record, dated.
          </>
        ),
      },
    ],
    related: ['concepts.labels', 'labeling.queue', 'labeling.pending', 'concepts.indicators'],
  },

  {
    id: 'page.analysis.replay',
    title: 'Replay',
    summary:
      'The same judgement with the future hidden. The session plays forward one bar at a time and you decide as it happens, so the label records a decision rather than a memory.',
    route: '/analysis/replay',
    sections: [
      {
        kind: 'prose',
        heading: 'Why this page exists',
        body: (
          <>
            Labelling with the whole session visible tells you what was <i>achievable</i>. This page
            tells you what was <i>decidable</i>, and the gap between the two is the size of the
            hindsight illusion measured on your own judgement. It is the ceiling on what any model
            could have captured in real time, and it is the single most useful number this project
            will produce — which is why a hundred sessions were held back before any hindsight
            labelling started. Those sessions, and only those, appear here.
          </>
        ),
      },
      {
        kind: 'steps',
        heading: 'The loop',
        items: [
          <>
            Press <b>Space</b> to reveal the next five-minute bar. The session starts at 09:45 and
            runs to 15:45.
          </>,
          <>
            Press <b>L</b> or <b>S</b> to open a position at the current bar. It fills at that
            bar&apos;s close.
          </>,
          <>
            Keep pressing <b>Space</b>. Both stops are live and are checked against every bar as it
            appears.
          </>,
          <>
            Press <b>X</b> to close at the current bar. Set confidence and tags first — they carry
            over between trades, as on the Labeling page.
          </>,
          <>
            Press <b>N</b> for the next reserved session, or <b>A</b> to let the clock run by itself
            while you are flat.
          </>,
        ],
      },
      {
        kind: 'prose',
        heading: 'The future is not on this page',
        body: (
          <>
            Not hidden, not greyed out — not sent. The server holds the cursor and refuses to return
            a bar, an indicator row or a daily bar dated later than it, whatever the browser asks
            for. That is stricter than it needs to be for honesty&apos;s sake and exactly as strict
            as it needs to be for the measurement: a replay label made while the future was visible
            looks identical to one made blind, and no later check can tell them apart.
          </>
        ),
      },
      {
        kind: 'terms',
        heading: 'The two stops',
        items: [
          {
            term: 'The 7% hard stop',
            body: (
              <>
                Priced at entry and frozen there. A breach closes the position at that bar with the
                exit recorded as a stop rather than as your decision.
              </>
            ),
          },
          {
            term: 'The 1.5 × ATR working stop',
            body: (
              <>
                The same, at one and a half times the average true range of the entry bar — so it is
                tighter on a quiet day and wider on a violent one. When a bar breaches both, the
                tighter one fills: at five-minute resolution which price came first inside the bar
                is unknowable, and assuming the kinder answer is how a backtest learns to survive
                trades that would really have stopped out. If the entry bar has no stored ATR there
                is no ATR stop, and the panel says so rather than inventing one.
              </>
            ),
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'Auto-advance stops when you are in a trade',
        body: (
          <>
            Deliberately. If the clock kept running while a position was open, the exit would become
            a reflex — you would let it drift because nothing prompted you — and an exit taken by
            inattention is not a decision. It resumes as soon as you are flat.
          </>
        ),
      },
      {
        kind: 'detail',
        heading: 'Under the hood',
        body: (
          <>
            The cursor is a column on the sitting and a database trigger refuses to move it
            backwards. Rewinding is restarting, which writes a new sitting naming the one it
            replaced together with your reason, so &ldquo;how many attempts did this session
            take&rdquo; survives into the analysis. A second trigger refuses any replay label whose
            exit is later than its sitting&apos;s cursor, and refuses a hindsight label on a
            reserved session outright — one hindsight pass burns a session for replay permanently,
            and the queue that hides them is a query rather than a control.
          </>
        ),
      },
    ],
    related: ['page.analysis.labeling', 'concepts.labels', 'concepts.indicators'],
  },

  {
    id: 'page.analysis.quality',
    title: 'Label quality',
    summary:
      'What is in the label dataset, what is wrong with it, and how much of a hindsight label was hindsight. The page to read before anything trains on these labels.',
    route: '/analysis/label-quality',
    sections: [
      {
        kind: 'terms',
        heading: 'The four views',
        items: [
          {
            term: 'Statistics',
            body: (
              <>
                What the dataset contains: counts by symbol, tag, direction, mode and month, the
                distribution of returns and R multiples, and the time of day you actually trade.
                Every figure carries the number of trades behind it.
              </>
            ),
          },
          {
            term: 'Quality',
            body: (
              <>
                Defects and outliers, kept apart. A defect is something the write path refuses, so
                an empty list is the expected result — the value of the check is that the day it is{' '}
                <i>not</i> empty, something wrote a label without going through the API. An outlier
                is legal and worth a second look.
              </>
            ),
          },
          {
            term: 'Agreement',
            body: (
              <>
                Where a session was labelled both blind and in hindsight, what the blind pass found,
                missed and invented. The most important number in this milestone.
              </>
            ),
          },
          {
            term: 'Drift',
            body: (
              <>
                How your own judgement moved: which labels you corrected, what changed, and how long
                the second look took.
              </>
            ),
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'The MFE/MAE scatter, and how to read it',
        body: (
          <>
            One point per label: maximum favourable excursion up the side, maximum adverse excursion
            along the bottom. Its <i>shape</i> answers a question no single statistic can. Points
            clustered high and to the right — good runs that never went far against you — say trail
            the stop and let them run. Points that reach far left before they reach up say your stop
            placement is the binding problem: a tighter stop would have taken you out of the trades
            that eventually worked. Colour is direction, and every point clicks through to its
            label.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'Why every number has a count next to it',
        body: (
          <>
            A 68% win rate over 19 trades is not a win rate; it is noise with a decimal point. A
            dashboard is exactly the place where a figure like that gets read as meaningful, then
            quoted, then built on — and a misread statistic about your own dataset is worse than no
            statistic, because it arrives with the authority of having been measured. So no figure
            here is shown without the population it was computed over, and the count is part of the
            value rather than a footnote beside it.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'What counts as the same trade',
        body: (
          <>
            Agreement has to decide when a blind trade and a hindsight trade are the same trade. The
            rule: same symbol, same session, same direction, and the two holding periods overlap.
            Not &ldquo;entered within a few bars&rdquo; — a blind entry twenty minutes after the
            hindsight one on the same move <i>is</i> the same trade found later, and that lateness
            is exactly what the timing figures measure. A tolerance would count it as one trade
            missed plus one invented, which doubles a single disagreement and makes the timing
            numbers look better by dropping the worst cases out of them.
          </>
        ),
      },
      {
        kind: 'detail',
        heading: 'Under the hood',
        body: (
          <>
            MFE and MAE are read from <code>labeling.label_outcome</code> and never recomputed here
            — a second implementation of a maximum excursion would disagree with the one the
            labelling page shows and nobody could say which was right. That table is filled by task
            C3, so the scatter is empty until then and says so. The export is content-hashed over
            the rows rather than over either file, so the CSV and the Parquet carry the same dataset
            name and an experiment can cite it; re-running over the same labels produces
            byte-identical files.
          </>
        ),
      },
    ],
    related: ['page.analysis.labeling', 'page.analysis.replay', 'concepts.labels'],
  },

  {
    id: 'page.admin.connections',
    title: 'Connections',
    summary:
      'Where third-party credentials are added and rotated — the broker key, and anything else the worker has to authenticate against.',
    route: '/admin/connections',
    sections: [
      {
        kind: 'prose',
        heading: 'How secrets are handled',
        body: (
          <>
            A credential entered here is encrypted immediately under a public key. The API that
            received it cannot read it back — only the worker holds the private key, and only the
            worker ever calls the service. What comes back to this screen is a masked hint, who
            added it and when, never the value.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'Rotating',
        body: (
          <>
            Adding a new credential for a service supersedes the previous one rather than
            overwriting it, so you can see what changed and when. Verify runs a real call against
            the provider as a job and reports whether the key actually works, rather than assuming
            it does because it was accepted.
          </>
        ),
      },
      {
        kind: 'prose',
        heading: 'Environment and feed',
        body: (
          <>
            Both are part of the credential, not a setting elsewhere. That is what stops a live
            environment being selected by accident, and it is why the data feed a job uses is
            decided by which credential it authenticates with.
          </>
        ),
      },
    ],
    related: ['concepts.paper-live', 'concepts.feeds'],
  },

  {
    id: 'page.admin.system',
    title: 'System health',
    summary:
      'Whether the worker is alive, what it is doing, and how deep the queue is. The first place to look when something has not happened.',
    route: '/admin/system',
    sections: [
      {
        kind: 'terms',
        heading: 'Worker states',
        items: [
          {
            term: 'Healthy',
            body: <>Reported in within the last couple of heartbeats. Jobs will be picked up.</>,
          },
          {
            term: 'Stale',
            body: (
              <>
                Has not reported recently. Usually a deployment in progress; if it persists, jobs
                are queuing up with nobody to run them.
              </>
            ),
          },
          {
            term: 'Down / unknown',
            body: (
              <>
                No worker is reporting at all. Anything enqueued will sit in the queue until one
                returns — nothing is lost, but nothing progresses.
              </>
            ),
          },
        ],
      },
      {
        kind: 'prose',
        body: (
          <>
            The version shown for the API and for the worker should match. A mismatch means a
            deployment is mid-flight or one of the two failed to update, which is worth resolving
            before trusting anything either of them reports.
          </>
        ),
      },
    ],
    related: ['concepts.jobs', 'page.data.jobs'],
  },
];

// ---- panel-level topics, for the ? beside a particular control -------------------------------

const PANELS: readonly HelpTopic[] = [
  {
    id: 'labeling.queue',
    title: 'The session queue',
    summary:
      'A worklist of sessions to label, and a position in it that is always derived rather than remembered.',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            The queue is a watchlist and a date range. Opening the page takes you to the first
            session with neither a label nor a skip on it — computed fresh every time, never stored.
            A remembered position drifts the moment anything else is written, and then &ldquo;resume
            where you left off&rdquo; puts you somewhere you have already been, which is how people
            stop trusting a queue.
          </>
        ),
      },
      {
        kind: 'terms',
        heading: 'Skipping',
        items: [
          {
            term: 'Nothing worth trading',
            body: (
              <>
                A judgement, and useful training data: a day you inspected and declined is a
                negative example.
              </>
            ),
          },
          {
            term: 'I cannot tell',
            body: <>An abstention. Not trained on as either a yes or a no.</>,
          },
          {
            term: 'The data looks wrong',
            body: <>A bug report about the feed that happens to arrive through this screen.</>,
          },
        ],
      },
      {
        kind: 'prose',
        heading: 'Held back sessions',
        body: (
          <>
            The count of sessions below the coverage gate is shown rather than hidden. A silent
            exclusion is indistinguishable from a range that was smaller than you thought.
          </>
        ),
      },
    ],
    related: ['page.analysis.labeling', 'concepts.coverage'],
  },

  {
    id: 'labeling.pending',
    title: 'The trade in progress',
    summary:
      'Everything that is about to be written, shown at all times, plus what the marked trade actually did.',
    sections: [
      {
        kind: 'prose',
        body: (
          <>
            Direction, confidence and tags are sticky — they carry from the last trade so you press
            them only when they change. That is where most of the speed comes from, and the price of
            it is that all three must be visible at all times, which is what this strip is for.
            Nothing is written until you press Enter.
          </>
        ),
      },
      {
        kind: 'terms',
        heading: 'The readout',
        items: [
          { term: 'ret', body: <>The return, in percent, in the direction of the trade.</> },
          {
            term: 'R',
            body: (
              <>
                The return in units of risk, where one R is 1.5 ATR from the entry. If the ATR has
                not warmed up yet there is no R at all, rather than a made-up one.
              </>
            ),
          },
          {
            term: 'MFE / MAE',
            body: (
              <>
                The best and the worst the trade ever looked while it was open — how much was left
                on the table, and how much heat it took first.
              </>
            ),
          },
          { term: 'bars', body: <>How many five-minute bars it was held for.</> },
        ],
      },
    ],
    related: ['page.analysis.labeling', 'concepts.labels', 'concepts.indicators'],
  },
];

export const HELP_TOPICS: readonly HelpTopic[] = [...CONCEPTS, ...PAGES, ...PANELS];

export const HELP_GROUPS: readonly { title: string; blurb: string; ids: readonly string[] }[] = [
  {
    title: 'Concepts',
    blurb: 'The vocabulary the rest of the system is built on. Worth reading once, in order.',
    ids: CONCEPTS.map((t) => t.id),
  },
  {
    title: 'Pages',
    blurb: 'What each screen is for and how to read what is on it.',
    ids: PAGES.map((t) => t.id),
  },
  {
    title: 'In detail',
    blurb: 'Parts of a screen that carry enough behaviour to be worth their own entry.',
    ids: PANELS.map((t) => t.id),
  },
];

export function topicById(id: string): HelpTopic | undefined {
  return HELP_TOPICS.find((t) => t.id === id);
}

/**
 * The topic for a URL path — the most specific match, so `/data/jobs/abc` lands on Jobs.
 *
 * Falls back to the overview rather than to nothing: a help button that sometimes opens an empty
 * panel teaches people not to press it.
 */
export function topicForRoute(path: string): HelpTopic {
  const clean = path.replace(/\/+$/, '') || '/';
  const matches = HELP_TOPICS.filter(
    (t) => t.route && (clean === t.route || clean.startsWith(`${t.route}/`)),
  );
  const best = matches.sort((a, b) => (b.route?.length ?? 0) - (a.route?.length ?? 0))[0];
  return best ?? topicById('concepts.overview')!;
}
